// Referral Service for SwipeSage
// Handles priority logic for apply links: Admin referral links > MITC source URLs

import { CreditCard } from '../firebase/schema';

export interface ApplyLinkResult {
  url: string;
  source: 'admin_referral' | 'mitc_source';
  notes?: string;
  isAvailable: boolean;
}

export class ReferralService {
  /**
   * Gets the appropriate apply link for a credit card
   * Priority: Admin referral link > MITC source URL
   */
  static getApplyLink(card: CreditCard): ApplyLinkResult {
    // Check for admin referral link first (highest priority)
    if (card.mitc?.referralLink?.url) {
      return {
        url: card.mitc.referralLink.url,
        source: 'admin_referral',
        notes: card.mitc.referralLink.notes,
        isAvailable: true,
      };
    }

    // Fallback to MITC source URL
    if (card.mitc?.sourceUrl) {
      return {
        url: card.mitc.sourceUrl,
        source: 'mitc_source',
        isAvailable: true,
      };
    }

    // No apply link available
    return {
      url: '',
      source: 'mitc_source',
      isAvailable: false,
    };
  }

  /**
   * Checks if any apply link is available for a card
   */
  static hasApplyLink(card: CreditCard): boolean {
    return !!(card.mitc?.referralLink?.url || card.mitc?.sourceUrl);
  }

  /**
   * Gets all cards with admin referral links
   */
  static getCardsWithReferralLinks(cards: CreditCard[]): CreditCard[] {
    return cards.filter(card => card.mitc?.referralLink?.url);
  }

  /**
   * Gets all cards with only MITC source URLs (no admin referral)
   */
  static getCardsWithMitcOnly(cards: CreditCard[]): CreditCard[] {
    return cards.filter(card => 
      card.mitc?.sourceUrl && !card.mitc?.referralLink?.url
    );
  }

  /**
   * Gets cards with no apply links available
   */
  static getCardsWithoutApplyLinks(cards: CreditCard[]): CreditCard[] {
    return cards.filter(card => 
      !card.mitc?.referralLink?.url && !card.mitc?.sourceUrl
    );
  }

  /**
   * Formats the apply link with tracking parameters if needed
   */
  static formatApplyLink(
    baseUrl: string, 
    options?: {
      userId?: string;
      cardId?: string;
      source?: string;
      utm_campaign?: string;
    }
  ): string {
    if (!baseUrl) return '';

    try {
      const url = new URL(baseUrl);
      
      // Add tracking parameters
      if (options?.userId) {
        url.searchParams.set('user_id', options.userId);
      }
      
      if (options?.cardId) {
        url.searchParams.set('card_id', options.cardId);
      }
      
      if (options?.source) {
        url.searchParams.set('utm_source', 'swipesage');
        url.searchParams.set('utm_medium', options.source);
      }
      
      if (options?.utm_campaign) {
        url.searchParams.set('utm_campaign', options.utm_campaign);
      }

      return url.toString();
    } catch (error) {
      // If URL parsing fails, return original URL
      console.warn('Invalid URL format:', baseUrl, error);
      return baseUrl;
    }
  }

  /**
   * Updates referral link for a card's MITC data
   */
  static updateReferralLink(
    card: CreditCard,
    referralUrl: string,
    notes?: string,
    addedBy?: string
  ): CreditCard {
    return {
      ...card,
      mitc: {
        ...card.mitc!,
        referralLink: {
          url: referralUrl,
          notes: notes || '',
          addedBy: addedBy || '',
          addedAt: new Date(),
        },
      },
      updatedAt: new Date(),
    };
  }

  /**
   * Removes referral link from a card's MITC data
   */
  static removeReferralLink(card: CreditCard): CreditCard {
    const { referralLink, ...mitcWithoutReferral } = card.mitc!;
    
    return {
      ...card,
      mitc: mitcWithoutReferral,
      updatedAt: new Date(),
    };
  }

  /**
   * Gets analytics data for referral links
   */
  static getReferralAnalytics(cards: CreditCard[]) {
    const totalCards = cards.length;
    const withReferral = this.getCardsWithReferralLinks(cards).length;
    const withMitcOnly = this.getCardsWithMitcOnly(cards).length;
    const withoutLinks = this.getCardsWithoutApplyLinks(cards).length;

    return {
      totalCards,
      withReferral,
      withMitcOnly,
      withoutLinks,
      referralCoverage: totalCards > 0 ? (withReferral / totalCards) * 100 : 0,
      applyLinkCoverage: totalCards > 0 ? ((withReferral + withMitcOnly) / totalCards) * 100 : 0,
    };
  }
}

export default ReferralService;